import { useCallback, useEffect, useRef } from 'react'

/**
 * Closing a click-to-edit box on blur changes its height. A press on another control (a dialog's
 * Save, a row's button) blurs the box on the press, so closing it then moves that control out from
 * under the pointer before the release, and the browser drops the click (USA-1647). While `active`,
 * the returned function closes at once unless a press is in progress, in which case it closes
 * after that press's click has run. Save the value before calling it.
 */
export function useCloseAfterPress(active: boolean) {
  const pressing = useRef(false)

  useEffect(() => {
    if (!active) return
    const press = () => {
      pressing.current = true
    }
    const release = () => {
      pressing.current = false
    }
    document.addEventListener('pointerdown', press, true)
    document.addEventListener('pointerup', release, true)
    document.addEventListener('pointercancel', release, true)
    return () => {
      document.removeEventListener('pointerdown', press, true)
      document.removeEventListener('pointerup', release, true)
      document.removeEventListener('pointercancel', release, true)
      pressing.current = false
    }
  }, [active])

  return useCallback((close: () => void) => {
    if (!pressing.current) {
      close()
      return
    }
    const afterPress = () => {
      document.removeEventListener('pointerup', afterPress, true)
      document.removeEventListener('pointercancel', afterPress, true)
      // The release's click is dispatched in the same task as pointerup.
      setTimeout(close)
    }
    document.addEventListener('pointerup', afterPress, true)
    document.addEventListener('pointercancel', afterPress, true)
  }, [])
}
